// NEW_FILE_JUSTIFIED: New route page src/pages/control/OrgDetailPage — no existing file
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated is the documented exception
import { StatCard } from '@/components/dashboard/StatCard';
import { PolicyBreakdownTable } from '@/components/control/PolicyBreakdownTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Building2, Users } from 'lucide-react';

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const orgId = id as Id<'organisations'>; // AS_JUSTIFIED: one-time Id cast from route param per convention

  const hierarchy = useQuery(api['superadmin/queries'].getUserHierarchy, { orgId });
  const analyticsResults = useQuery(api['superadmin/queries'].getPolicyAnalyticsByOrg, { orgId });

  const analytics = analyticsResults?.[0];
  const isHierarchyLoading = hierarchy === undefined;
  const isAnalyticsLoading = analyticsResults === undefined;

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/control/organisations')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold text-foreground">
          {hierarchy ? hierarchy.orgName : 'Organisation Detail'}
        </h1>
      </div>

      {hierarchy && (
        <p className="text-sm text-muted-foreground -mt-4">
          Owner: {hierarchy.owner.name ?? 'Unknown'}
          {hierarchy.owner.email ? ` · ${hierarchy.owner.email}` : ''}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total Policies"
          value={analytics?.totalPolicies}
          subtitle="Across this organisation"
        />
        <StatCard
          label="Companies"
          value={isHierarchyLoading ? undefined : (hierarchy?.companies.length ?? 0)}
          subtitle="Active company accounts"
        />
        <StatCard
          label="Total Members"
          value={
            isHierarchyLoading
              ? undefined
              : (hierarchy?.companies.reduce((sum, c) => sum + c.members.length, 0) ?? 0)
          }
          subtitle="Across all companies"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Policy Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0 pb-4">
          <PolicyBreakdownTable breakdown={analytics?.breakdown} isLoading={isAnalyticsLoading} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Companies
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isHierarchyLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : !hierarchy || hierarchy.companies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No companies found</p>
          ) : (
            <div className="flex flex-col gap-3">
              {hierarchy.companies.map(company => (
                <div
                  key={company._id}
                  className="flex items-start justify-between border rounded-md px-4 py-3"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-medium text-sm">{company.name}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {company.members.length} member{company.members.length !== 1 ? 's' : ''} ·{' '}
                      {company.employeeCount} employee{company.employeeCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <Badge variant={company.status === 'active' ? 'default' : 'secondary'}>
                    {company.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
